/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { ToggleMenu } from '@opengeoweb/shared';
import { Copy, Delete, ZoomToLayer } from '@opengeoweb/theme';
import type { STACGeoTIFFLayerOptions } from '@opengeoweb/webmap';
import { useCoreTranslation } from '../../../../../utils/i18n';
import SelectableFeaturesCheckboxConnect from '../SelectableFeaturesCheckbox/SelectableFeaturesCheckboxConnect';

interface LayerManagerMenuButtonProps {
  mapId: string;
  layerId?: string;
  isFeatureLayer?: boolean;
  isFeatureSyncLayer?: boolean;
  onLayerDuplicate?: (payload: { mapId: string; layerId: string }) => void;
  onLayerCopyToOtherMaps?: () => void;
  onLayerRemoveFromOtherMaps?: () => void;
  onLayerZoomToExtent?: () => void;
  canZoomToLayerExtent?: boolean;
  tooltipTitle?: string;
  icon?: React.ReactNode;
  stacDataLayout?: STACGeoTIFFLayerOptions['dataLayout'];
  isTiledStacCollection?: boolean;
  onChangeStacDataLayout?: (
    dataLayout: NonNullable<STACGeoTIFFLayerOptions['dataLayout']>,
  ) => void;
}

const LayerManagerMenuButton: React.FC<LayerManagerMenuButtonProps> = ({
  mapId,
  layerId,
  isFeatureLayer = false,
  isFeatureSyncLayer = false,
  onLayerDuplicate,
  onLayerCopyToOtherMaps,
  onLayerRemoveFromOtherMaps,
  onLayerZoomToExtent,
  canZoomToLayerExtent = true,
  tooltipTitle,
  icon,
  stacDataLayout,
  isTiledStacCollection,
  onChangeStacDataLayout,
}: LayerManagerMenuButtonProps) => {
  const { t } = useCoreTranslation();
  const nonEmptyTooltipTitle = tooltipTitle ?? t('layermanager-options');

  const onClickDuplicate = (): void => {
    onLayerDuplicate!({ mapId, layerId: layerId! });
  };

  const onClickCopyToOtherMaps = (): void => {
    onLayerCopyToOtherMaps?.();
  };

  const onClickRemoveFromOtherMaps = (): void => {
    onLayerRemoveFromOtherMaps?.();
  };

  const onClickZoomToExtent = (): void => {
    onLayerZoomToExtent?.();
  };

  const stacDataLayoutMenuItem = onChangeStacDataLayout
    ? {
        text: t('layermanager-stac-layout-title'),
        action: (): void => {},
        subMenuItems: [
          {
            text: t('layermanager-stac-layout-auto'),
            action: (): void => onChangeStacDataLayout('auto'),
            controlType: 'radio' as const,
            isSelected: !stacDataLayout || stacDataLayout === 'auto',
          },
          // Temporal mode is not supported for spatially tiled collections
          ...(!isTiledStacCollection
            ? [
                {
                  text: t('layermanager-stac-layout-temporal'),
                  action: (): void => onChangeStacDataLayout('temporal'),
                  controlType: 'radio' as const,
                  isSelected: stacDataLayout === 'temporal',
                },
              ]
            : []),
          {
            text: t('layermanager-stac-layout-spatial-tiles'),
            action: (): void => onChangeStacDataLayout('spatial-tiles'),
            controlType: 'radio' as const,
            isSelected: stacDataLayout === 'spatial-tiles',
          },
        ],
      }
    : undefined;

  return (
    <ToggleMenu
      buttonIcon={icon}
      buttonSx={{
        margin: '4px 2px',
        '&.MuiButtonBase-root': {
          backgroundColor: 'transparent',
          color: 'geowebColors.greys.accessible',
        },
      }}
      buttonTestId="openMenuButton"
      menuItems={[
        {
          text: t('layermanager-layer-duplicate'),
          action: onClickDuplicate,
          icon: <Copy />,
          isDisabled: isFeatureSyncLayer,
        },
        {
          text: t('layermanager-layer-duplicate-to-other-maps'),
          action: onClickCopyToOtherMaps,
          icon: <Copy />,
          isDisabled: isFeatureSyncLayer,
        },
        {
          text: t('layermanager-layer-remove-from-other-maps'),
          action: onClickRemoveFromOtherMaps,
          icon: <Delete />,
          isDisabled: isFeatureSyncLayer,
        },
        {
          text: t('layermanager-layer-zoom-to-extent'),
          action: onClickZoomToExtent,
          icon: <ZoomToLayer />,
          isDisabled: !canZoomToLayerExtent,
        },
        ...(stacDataLayoutMenuItem ? [stacDataLayoutMenuItem] : []),
      ]}
      menuPosition="bottom"
      tooltipTitle={nonEmptyTooltipTitle}
    >
      {!!isFeatureLayer && (
        <SelectableFeaturesCheckboxConnect
          disabled={isFeatureSyncLayer}
          layerId={layerId!}
        />
      )}
    </ToggleMenu>
  );
};

export default LayerManagerMenuButton;
